import { PageLayout } from '../../layout/PageLayout'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useTranslations } from '@i18n/useLanguage'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { MapPageHeader } from '@app/components/blocks/pageHeader/MapPageHeader'
import {
  faPlus,
  faCheck,
  faArrowLeft,
} from '@fortawesome/pro-regular-svg-icons'
import { RegionTabContent } from './regionTabs/RegionTabContent'
import { DiscoverPageLayout } from '../../layout/DiscoverPageLayout'
import { ItemListCard } from '@app/components/blocks/card/ItemListCard'
import { navigate } from 'wouter/use-browser-location'
import { pages } from '@app/util/links'
import { RegionViewModel } from 'functions/app/geography/view/region'
import { Head, useAuthState, useClient, useQuery } from '@based/sdk/react'
import { enrich } from '@i18n/localizeHelper'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { LockedPageCover } from '@app/components/blocks/lockedPageCover/LockedPageCover'
import useMediaQuery from '@app/util/useMediaQuery'
import { ServiceProviderListRegionalModel } from 'functions/app/service-provider/list-regional'
import { RegionalHousingFormsCard } from './RegionalHousingFormsCard'
import { SERVICE_PROVIDER_TYPES } from '@schema/serviceProvider'

export type RegionPageProps = {
  slug: string
}

export const RegionPage = ({ slug }: RegionPageProps) => {
  const isMobile = useMediaQuery()
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const translations = useTranslations().app
  const regionQuery = useQuery('view-region', { slug })
  const { withAuthCheck } = useAuthModal()
  const client = useClient()
  const authState = useAuthState()

  useEffect(() => {
    setMounted(true)
  }, [])

  const region: RegionViewModel = regionQuery.data

  const pageManager = region?.pageManager
  const housingFormsQuery = useQuery('list-regional-housing-forms', {
    regionId: region?.id,
    top: 5,
  })

  const serviceProvidersQuery = useQuery('list-regional-service-providers', {
    regionId: region?.id,
    top: 5,
    types: [
      SERVICE_PROVIDER_TYPES.network,
      SERVICE_PROVIDER_TYPES.supportGroup,
    ],
  })
  const followerCountQuery = useQuery('count-followers', {
    entityType: 'region',
    entityIds: region?.id ? [region?.id] : [],
  })

  const municipalityItems = useMemo(
    () =>
      (region?.municipalities ?? [])
        .sort((a, b) => b.followers.count - a.followers.count)
        .map((m) => ({
          image: m.image?.src,
          title: m.name,
          subtitle: enrich(
            translations.common.followersAmount,
            m.followers.count,
          ),
          isVerified: m.crowdbuildingPartner ?? false,
          onClick: () => navigate(pages.municipalityPage(m.slug)),
        })),
    [region?.municipalities, translations.common.followersAmount],
  )

  const serviceProviders = useMemo(
    () =>
      (serviceProvidersQuery.data ?? []) as ServiceProviderListRegionalModel[],
    [serviceProvidersQuery.data],
  )
  const serviceProviderItems = useMemo(
    () =>
      serviceProviders
        .slice(0, 5)
        .map((sp) => ({
          image: sp.avatar?.src ?? '',
          title: sp.name,
          subtitle: enrich(
            translations.common.followersAmount,
            sp.followers.count,
          ),
          followerCount: sp.followers.count,
          onClick: () => navigate(pages.serviceProvider(sp.slug)),
        }))
        .sort((a, b) => b.followerCount - a.followerCount),
    [serviceProviders, translations.common.followersAmount],
  )

  const followRegion = useCallback(() => {
    if (!region?.id) return

    void withAuthCheck(async () => {
      await client.call('follow-region', {
        regionId: region.id,
      })
    })
  }, [withAuthCheck, client, region?.id])

  const isFollowingQuery = useQuery(authState?.userId ? 'is-following' : null, {
    entityType: 'region',
    entityId: region?.id,
    userId: Number(authState?.userId),
  })
  const isFollowing = mounted && ((isFollowingQuery.data as boolean) ?? false)
  // Use stable icon and label until after mount to avoid hydration mismatch (auth state differs server vs client)
  const followButtonIcon = mounted ? (isFollowing ? faCheck : faPlus) : faPlus
  const followButtonLabel = mounted
    ? isFollowing
      ? translations.common.following
      : translations.common.follow
    : translations.common.follow

  const mapHeaderCoordinates = useMemo(
    () =>
      region?.mapCoordinates?.lat && region?.mapCoordinates?.long
        ? { lat: region.mapCoordinates.lat, long: region.mapCoordinates.long }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [region?.mapCoordinates?.lat, region?.mapCoordinates?.long],
  )

  const mapHeaderButtons = useMemo(
    () => [
      {
        label: translations.common.share,
        onClick: () => setIsShareModalOpen(true),
        variant: 'secondary' as const,
        size: isMobile ? ('small' as const) : ('medium' as const),
      },
      {
        label: followButtonLabel,
        onClick: followRegion,
        variant: 'primary' as const,
        size: isMobile ? ('small' as const) : ('medium' as const),
        icon: followButtonIcon,
        iconPosition: 'left' as const,
      },
    ],
    [
      translations.common.share,
      isMobile,
      followButtonLabel,
      followRegion,
      followButtonIcon,
    ],
  )

  const mapHeaderOnClick = useMemo(
    () =>
      pageManager
        ? () => navigate(pages.regionalBusinessProfile(pageManager.slug))
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageManager?.slug],
  )

  if (!region) {
    return (
      <>
        <PageLayout gap="2rem">
          <Row style={{ alignItems: 'flex-start' }} gap="3rem">
            <Column
              gap="1.5rem"
              alignItems="stretch"
              style={{ flex: '2 1 auto' }}
            >
              <MapPageHeader title={translations.common.loading} />
            </Column>
          </Row>
        </PageLayout>
      </>
    )
  }

  const mapHeader = (
    <MapPageHeader
      title={region.name}
      subtitle={pageManager ? translations.regionPage.managedBy : null}
      profile={pageManager}
      hasBadge={region.crowdbuildingPartner}
      buttons={mapHeaderButtons}
      onClick={mapHeaderOnClick}
      followers={region.followers}
      totalFollowers={(followerCountQuery.data as number[])?.[0] ?? 0}
      coordinates={mapHeaderCoordinates}
      zoom={8}
    />
  )

  if (isMobile) {
    return (
      <>
        <PageLayout gap="0rem" standardPageTitle={false}>
          <Head>
            <title>{`${region.name} | CrowdBuilding`}</title>
          </Head>
          <Column alignItems="stretch">
            {mapHeader}
            <ItemListCard
              title={translations.regionPage.municipalities}
              items={municipalityItems}
            />
            <RegionalHousingFormsCard
              regionId={region.id}
              spotlightedHousingForms={region.spotlightedHousingForms}
              entityName={region.name}
            />
            {serviceProviderItems.length > 0 && (
              <ItemListCard
                title={`${translations.regionPage.networksAndSupportGroupsIn} ${region.name}`}
                items={serviceProviderItems}
              />
            )}
          </Column>
          <RegionTabContent region={region} />
        </PageLayout>

        <SocialShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          title={region.name}
        />

        {region.crowdbuildingPartner === false &&
          (isFollowing ? (
            <LockedPageCover
              title={translations.regionPage.lockedProvinceFollowed}
              body={translations.regionPage.lockedProvinceBodyFollowed}
              buttonProps={[
                {
                  variant: 'secondary',
                  size: 'medium',
                  label: translations.common.back,
                  onClick: () => navigate(pages.regions),
                  icon: faArrowLeft,
                  iconPosition: 'left',
                },
                {
                  variant: 'primary',
                  size: 'medium',
                  label: translations.regionPage.pageFollowed,
                  onClick: followRegion,
                  icon: faCheck,
                  iconPosition: 'left',
                },
              ]}
              style={{
                inset: '3rem 0 0 0',
              }}
            />
          ) : (
            <LockedPageCover
              title={enrich(
                translations.regionPage.lockedProvinceHeader,
                region.name,
              )}
              body={enrich(
                translations.regionPage.lockedProvinceBody,
                region.name,
              )}
              buttonProps={[
                {
                  variant: 'primary',
                  size: 'medium',
                  label: translations.regionPage.followThePage,
                  onClick: followRegion,
                },
              ]}
              style={{
                inset: '3rem 0 0 0',
              }}
            />
          ))}
      </>
    )
  }

  return (
    <>
      <PageLayout gap="2rem" standardPageTitle={false}>
        <Head>
          <title>{`${region.name} | CrowdBuilding`}</title>
        </Head>
        <DiscoverPageLayout
          sidebar={
            <>
              <ItemListCard
                title={translations.regionPage.municipalities}
                items={municipalityItems}
              />
              <RegionalHousingFormsCard
                regionId={region.id}
                spotlightedHousingForms={region.spotlightedHousingForms}
                entityName={region.name}
              />
              {serviceProviderItems.length > 0 && (
                <ItemListCard
                  title={`${translations.regionPage.networksAndSupportGroupsIn} ${region.name}`}
                  items={serviceProviderItems}
                />
              )}
            </>
          }
        >
          {mapHeader}
          <RegionTabContent region={region} />
        </DiscoverPageLayout>
      </PageLayout>

      <SocialShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={region.name}
      />

      {region.crowdbuildingPartner === false &&
        (isFollowing ? (
          <LockedPageCover
            title={translations.regionPage.lockedProvinceFollowed}
            body={translations.regionPage.lockedProvinceBodyFollowed}
            buttonProps={[
              {
                variant: 'secondary',
                size: 'medium',
                label: translations.common.back,
                onClick: () => navigate(pages.regions),
                icon: faArrowLeft,
                iconPosition: 'left',
              },
              {
                variant: 'primary',
                size: 'medium',
                label: translations.regionPage.pageFollowed,
                onClick: followRegion,
                icon: faCheck,
                iconPosition: 'left',
              },
            ]}
            style={{
              inset: 'var(--header-height) 0 0 0',
            }}
          />
        ) : (
          <LockedPageCover
            title={enrich(
              translations.regionPage.lockedProvinceHeader,
              region.name,
            )}
            body={enrich(
              translations.regionPage.lockedProvinceBody,
              region.name,
            )}
            buttonProps={[
              {
                variant: 'primary',
                size: 'medium',
                label: translations.regionPage.followThePage,
                onClick: followRegion,
              },
            ]}
            style={{
              inset: 'var(--header-height) 0 0 0',
            }}
          />
        ))}
    </>
  )
}
